'use client';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ChainNetwork } from '@ducat-unit/core';
import type { AddressPurpose, AddressType } from '@sats-connect/core';

import type { LiquidationChartDataItem } from '@/app/liquidations/components/liquidation.utils';
import type { ILiquidVaultExtended } from '@/hooks/_v3/types';
import type { UserAccountAction } from '@/types/types';

export enum WalletType {
  Xverse = 'xverse',
  Unisat = 'unisat',
  Metamask = 'metamask',
}

export enum WalletTypeExtended {
  Xverse = 'xverse',
  Unisat = 'unisat',
  Leather = 'leather',
  Metamask = 'metamask',
  // MagicEden = 'magiceden',
}

export interface Address {
  address: string;
  publicKey: string;
  purpose: AddressPurpose;
  addressType: AddressType;
}

export interface Balance {
  confirmed: string;
  unconfirmed: string;
  total: string;
}

export interface WalletInfo {
  type: WalletType;
  address: string;
  network: ChainNetwork;
  publicKey?: string;
}

export interface WalletContextType {
  isConnected: boolean;
  walletInfo: WalletInfo | null;
  balance: Balance | null;
  // connect: (walletType: WalletType) => Promise<void>;
  disconnect: () => Promise<void>;
  isMainnet: boolean;
  toggleNetwork: () => void;
  activeWallet: WalletType | null;
  setActiveWallet: React.Dispatch<React.SetStateAction<WalletType | null>>;
  connectionError: boolean;
}

export type LiquidationSwapOutcome =
  | { status: 'not-requested' }
  | { status: 'failed' }
  | { status: 'submitted'; txid: string; btcAmount: number; unitAmount: number };

export interface TransactionHistoryItem {
  date: string;
  type: UserAccountAction;
  amount: number;
  amountUnit: 'UNIT' | 'BTC';
  amount2?: number;
  amount2Unit?: 'UNIT' | 'BTC';
  status: 'In Progress' | 'Completed' | 'Dropped';
  healthFactorBefore: number | null;
  healthFactorAfter?: number | null;
  healthStatusBefore: {
    statusColor: string;
    rangeColor: string;
    status: string;
  };
  healthStatusAfter?: {
    statusColor: string;
    rangeColor: string;
    status: string;
  };
  transactionId: string;
  btcInVault?: number;
  unitInVault?: number;
  vault_txid?: string;
  vault_root_txid?: string;
  isFullRepay?: boolean;
  liquidationSwap?: LiquidationSwapOutcome;
  relatedLiquidationTxid?: string;
}

export type LiquidationContext = {
  isAutomaticSwap: boolean;
  isLiquidatedLoading: boolean;
  liquidationError?: Error | null;
  btcPrice: number;
  claimedItems: ILiquidVaultExtended[];
  liquidVaultsExtended: ILiquidVaultExtended[];
  chartData: LiquidationChartDataItem[];
  totalClaimable: number;
  totalClaimableUnits: number;
  claimAmount: number;
  investAmount: number;
  claimedDebtUnits: number;
  rowSelection: Record<string, boolean>;
  estimatedYield: {
    btc: number;
    percent: number;
  };
  // onClaimAmountChange: (amount: number) => void;
  onInvestAmountChange: (amount: number) => void;
  resetClaimAmount: () => void;
  updateAutomaticSwap: (value: boolean) => void;
  setAlreadyClaimedIds: Dispatch<SetStateAction<string[]>>;
};
